import React, { useEffect, useRef, useState } from "react";
import feaIcon01 from "../../images/icon/fea-small-icon01.svg";
import feaIcon02 from "../../images/icon/fea-small-icon02.svg";
import feaIcon03 from "../../images/icon/fea-small-icon03.svg";
import feaIcon04 from "../../images/icon/fea-small-icon04.svg";
import feaIcon05 from "../../images/icon/fea-small-icon05.svg";
import feaIcon06 from "../../images/icon/fea-small-icon06.svg";
import diamondIcon from "../../images/icon/diamond-icon02.gif";

const steps = [
  {
    num: "01",
    icon: feaIcon01,
    title: "Discovery Call",
    desc: "30-min session to map your workflows and goals.",
    color: "#00ff97",
    delay: "0ms",
  },
  {
    num: "02",
    icon: feaIcon02,
    title: "Automation Audit",
    desc: "We rank every automation opportunity by ROI.",
    color: "#00D9F5",
    delay: "100ms",
  },
  {
    num: "03",
    icon: feaIcon03,
    title: "Proposal & Scope",
    desc: "Fixed price, clear timeline. Zero surprises.",
    color: "#A855F7",
    delay: "200ms",
  },
  {
    num: "04",
    icon: feaIcon04,
    title: "Build & Deploy",
    desc: "We ship into your live stack in days, not months.",
    color: "#00ff97",
    delay: "300ms",
  },
  {
    num: "05",
    icon: feaIcon05,
    title: "Test & Handover",
    desc: "Full QA + team training before we hand the keys.",
    color: "#00D9F5",
    delay: "400ms",
  },
  {
    num: "06",
    icon: feaIcon06,
    title: "Ongoing Support",
    desc: "We monitor, optimise, and scale with you 24/7.",
    color: "#A855F7",
    delay: "500ms",
  },
];

const AboutSection: React.FC = () => {
  const [activeStep, setActiveStep]       = useState(0);
  const [visible, setVisible]             = useState(false);
  const [panelFade, setPanelFade]         = useState(true);   // controls detail-panel opacity
  const [isDesktop, setIsDesktop]         = useState(
    typeof window !== "undefined" ? window.innerWidth >= 768 : true
  );
  const sectionRef   = useRef<HTMLElement>(null);
  const intervalRef  = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Responsive breakpoint (debounced) ──────────────────────────────────────
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const onResize = () => {
      clearTimeout(timer);
      timer = setTimeout(() => setIsDesktop(window.innerWidth >= 768), 150);
    };
    window.addEventListener("resize", onResize);
    return () => { window.removeEventListener("resize", onResize); clearTimeout(timer); };
  }, []);

  // ── Auto-cycle (desktop only) — fade out → swap → fade in ─────────────────
  const goToStep = (idx: number) => {
    setPanelFade(false);
    setTimeout(() => {
      setActiveStep(idx);
      setPanelFade(true);
    }, 200); // matches CSS transition duration
  };

  // ── Auto-cycle using a ref so the interval always sees the latest step ──────
  const activeStepRef = useRef(activeStep);
  activeStepRef.current = activeStep;

  useEffect(() => {
    if (!isDesktop) return;
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      const next = (activeStepRef.current + 1) % steps.length;
      setPanelFade(false);
      setTimeout(() => {
        setActiveStep(next);
        setPanelFade(true);
      }, 200);
    }, 2800);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [isDesktop]);

  // ── Entrance animation trigger ─────────────────────────────────────────────
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const step = steps[activeStep];

  // Manual step change (stops and restarts the interval)
  const handleManualStep = (idx: number) => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    goToStep(idx);
    // restart auto-cycle
    intervalRef.current = setInterval(() => {
      const next = (activeStepRef.current + 1) % steps.length;
      setPanelFade(false);
      setTimeout(() => { setActiveStep(next); setPanelFade(true); }, 200);
    }, 2800);
  };

  return (
    <section
      className="pos-rel"
      id="how-we-work"
      ref={sectionRef}
      style={{ background: "#00020f", overflow: "hidden", padding: "clamp(60px,10vw,145px) 0" }}
    >
      {/* Grid bg */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute", inset: 0,
          backgroundImage: "linear-gradient(rgba(0,255,151,0.025) 1px,transparent 1px),linear-gradient(90deg,rgba(0,255,151,0.025) 1px,transparent 1px)",
          backgroundSize: "80px 80px",
          WebkitMaskImage: "radial-gradient(ellipse 80% 60% at 50% 50%,black 40%,transparent 100%)",
          maskImage: "radial-gradient(ellipse 80% 60% at 50% 50%,black 40%,transparent 100%)",
          pointerEvents: "none",
        }}
      />

      <div className="container" style={{ position: "relative", zIndex: 2 }}>

        {/* Header */}
        <div className="sec-title sec-title-center text-center mb-80 wow fadeInUp" data-wow-duration="800ms">
          <span className="sub-title mb-15">How We Work</span>
          <h2 className="title" style={{ textAlign: "center", lineHeight: 1.35 }}>
            From discovery{" "}
            <img src={diamondIcon} alt="" aria-hidden="true"
              style={{ width: 36, verticalAlign: "middle", display: "inline", margin: "0 4px" }}
            />
            {" "}to deployment
          </h2>
          <p style={{ color: "var(--color-gray)", maxWidth: 560, margin: "16px auto 0", lineHeight: 1.75, fontSize: "1.05rem" }}>
            A battle-tested 6-phase process — transparent, fast, and designed to get you live in weeks.
          </p>
        </div>

        {/* ══════════════════ DESKTOP ══════════════════ */}
        {isDesktop && (
          <>
            {/* Horizontal step roadmap */}
            <div style={{ display: "flex", justifyContent: "center", gap: 0, position: "relative" }}>
              {steps.map((s, idx) => {
                const isActive = activeStep === idx;
                const isPast   = idx < activeStep;
                return (
                  <React.Fragment key={idx}>
                    <button
                      onClick={() => handleManualStep(idx)}
                      style={{
                        display: "flex", flexDirection: "column", alignItems: "center", gap: 14,
                        background: "transparent", border: "none", cursor: "pointer",
                        padding: "0 8px", minWidth: 130,
                        opacity: visible ? 1 : 0,
                        transform: visible ? "translateY(0)" : "translateY(20px)",
                        transition: `opacity 0.5s ease ${s.delay}, transform 0.5s ease ${s.delay}`,
                      }}
                    >
                      <div style={{
                        width: isActive ? 68 : 52, height: isActive ? 68 : 52,
                        borderRadius: "50%",
                        border: `2px solid ${isActive ? s.color : isPast ? s.color + "60" : "rgba(255,255,255,0.1)"}`,
                        background: isActive
                          ? `radial-gradient(circle, ${s.color}22, transparent 70%)`
                          : "rgba(255,255,255,0.02)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        transition: "all 0.4s ease",
                        boxShadow: isActive ? `0 0 24px ${s.color}50, 0 0 0 6px ${s.color}12` : "none",
                        flexShrink: 0,
                      }}>
                        <img src={s.icon} alt={s.title} style={{
                          width: isActive ? 26 : 20, height: isActive ? 26 : 20,
                          transition: "all 0.4s ease",
                          filter: `brightness(${isActive ? 1 : isPast ? 0.7 : 0.4})`,
                        }} />
                      </div>
                      <span style={{
                        fontFamily: "'Courier New', monospace", fontSize: "0.62rem", fontWeight: 700,
                        letterSpacing: 2,
                        color: isActive ? s.color : isPast ? s.color + "80" : "rgba(255,255,255,0.2)",
                        transition: "color 0.4s ease",
                      }}>{s.num}</span>
                      <span style={{
                        fontSize: "0.8rem", fontWeight: isActive ? 700 : 400,
                        color: isActive ? "#fff" : "var(--color-gray)",
                        textAlign: "center", lineHeight: 1.3, transition: "all 0.4s ease",
                      }}>{s.title}</span>
                    </button>

                    {idx < steps.length - 1 && (
                      <div style={{
                        flex: 1, height: 2, alignSelf: "flex-start", marginTop: 33,
                        background: idx < activeStep
                          ? `linear-gradient(90deg, ${s.color}80, ${steps[idx + 1].color}80)`
                          : "rgba(255,255,255,0.06)",
                        transition: "background 0.6s ease",
                        position: "relative", minWidth: 16,
                      }}>
                        {idx === activeStep - 1 && (
                          <div style={{
                            position: "absolute", top: "50%", right: 0,
                            transform: "translateY(-50%)", width: 8, height: 8,
                            borderRadius: "50%", background: steps[idx + 1].color,
                            boxShadow: `0 0 12px ${steps[idx + 1].color}`,
                          }} />
                        )}
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            {/* Detail panel — fades when step changes, fixed height prevents layout shifts */}
            <div style={{
              marginTop: 52,
              background: "rgba(255,255,255,0.025)",
              border: `1px solid ${step.color}30`,
              borderTop: `3px solid ${step.color}`,
              borderRadius: 20,
              padding: "40px 44px",
              display: "flex", gap: 40, alignItems: "center", flexWrap: "wrap",
              position: "relative", overflow: "hidden",
              transition: "border-color 0.4s ease",
              minHeight: 220,
              willChange: "border-color",
              contain: "layout",
            }}>
              {/* Glow */}
              <div aria-hidden="true" style={{
                position: "absolute", top: -80, left: -80,
                width: 300, height: 300, borderRadius: "50%",
                background: `radial-gradient(circle, ${step.color}12 0%, transparent 70%)`,
                pointerEvents: "none", transition: "all 0.5s ease",
              }} />

              {/* Fading content wrapper — use a real block element so opacity works */}
              <div style={{
                display: "flex", gap: 40, alignItems: "center", flexWrap: "wrap",
                flex: 1, opacity: panelFade ? 1 : 0,
                transition: "opacity 0.2s ease",
              }}>
                {/* Large number */}
                <div style={{
                  fontFamily: "var(--font-heading)",
                  fontSize: "clamp(4rem, 9vw, 8rem)",
                  fontWeight: 800, color: `${step.color}18`,
                  lineHeight: 1, userSelect: "none", flexShrink: 0,
                }}>{step.num}</div>

                {/* Icon + text */}
                <div style={{ flex: 1, minWidth: 240 }}>
                  <div style={{
                    width: 60, height: 60, borderRadius: 15,
                    background: `${step.color}14`, border: `1px solid ${step.color}30`,
                    display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 18,
                  }}>
                    <img src={step.icon} alt={step.title} style={{ width: 30 }} />
                  </div>
                  <h3 style={{
                    fontFamily: "var(--font-heading)",
                    fontSize: "clamp(1.4rem, 2.5vw, 2.2rem)",
                    fontWeight: 800, color: "#fff", letterSpacing: "-0.04em",
                    marginBottom: 12, marginTop: 0,
                  }}>{step.title}</h3>
                  <p style={{ color: "var(--color-gray)", fontSize: "1rem", lineHeight: 1.75, maxWidth: 480 }}>
                    {step.desc}
                  </p>
                </div>

                {/* Prev / Next buttons */}
                <div style={{ display: "flex", flexDirection: "column", gap: 12, flexShrink: 0 }}>
                  <button
                    onClick={() => handleManualStep(Math.max(0, activeStep - 1))}
                    disabled={activeStep === 0}
                    aria-label="Previous step"
                    style={{
                      width: 46, height: 46, borderRadius: "50%",
                      background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)",
                      color: "#fff", fontSize: "1.1rem", cursor: activeStep === 0 ? "not-allowed" : "pointer",
                      opacity: activeStep === 0 ? 0.3 : 1, transition: "all 0.2s",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}
                  >↑</button>
                  <button
                    onClick={() => handleManualStep(Math.min(steps.length - 1, activeStep + 1))}
                    disabled={activeStep === steps.length - 1}
                    aria-label="Next step"
                    style={{
                      width: 46, height: 46, borderRadius: "50%",
                      background: step.color + "20", border: `1px solid ${step.color}40`,
                      color: step.color, fontSize: "1.1rem",
                      cursor: activeStep === steps.length - 1 ? "not-allowed" : "pointer",
                      opacity: activeStep === steps.length - 1 ? 0.3 : 1, transition: "all 0.2s",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}
                  >↓</button>
                </div>
              </div>
            </div>

            {/* Dot indicators */}
            <div style={{ textAlign: "center", marginTop: 24, display: "flex", justifyContent: "center", gap: 8 }}>
              {steps.map((s, idx) => (
                <button key={idx} onClick={() => handleManualStep(idx)} style={{
                  width: idx === activeStep ? 22 : 7, height: 7, borderRadius: 4,
                  background: idx === activeStep ? s.color : "rgba(255,255,255,0.1)",
                  border: "none", cursor: "pointer", padding: 0, transition: "all 0.3s ease",
                }} />
              ))}
            </div>
          </>
        )}

        {/* ══════════════════ MOBILE — 2-column card grid ══════════════════ */}
        {!isDesktop && (
          <div>
            {/* 2×3 grid */}
            <div style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 12,
            }}>
              {steps.map((s, idx) => (
                <div
                  key={idx}
                  style={{
                    background: "rgba(255,255,255,0.025)",
                    border: "1px solid rgba(255,255,255,0.07)",
                    borderTop: `3px solid ${s.color}`,
                    borderRadius: 14,
                    padding: "18px 16px",
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                    opacity: visible ? 1 : 0,
                    transform: visible ? "translateY(0)" : "translateY(16px)",
                    transition: `opacity 0.5s ease ${s.delay}, transform 0.5s ease ${s.delay}`,
                  }}
                >
                  {/* Icon + number row */}
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{
                      width: 40, height: 40, borderRadius: 10,
                      background: `rgba(${s.color === "#00ff97" ? "0,255,151" : s.color === "#00D9F5" ? "0,217,245" : "168,85,247"},0.12)`,
                      border: `1px solid ${s.color}30`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <img src={s.icon} alt={s.title} style={{ width: 20 }} />
                    </div>
                    <span style={{
                      fontFamily: "'Courier New', monospace",
                      fontSize: "1.6rem", fontWeight: 800,
                      color: `${s.color}18`, lineHeight: 1,
                      userSelect: "none",
                    }}>{s.num}</span>
                  </div>

                  {/* Title */}
                  <h3 style={{
                    fontSize: "0.88rem", fontWeight: 700, color: "#fff",
                    margin: 0, lineHeight: 1.3,
                  }}>{s.title}</h3>

                  {/* Description */}
                  <p style={{
                    fontSize: "0.78rem", color: "rgba(255,255,255,0.5)",
                    lineHeight: 1.55, margin: 0,
                  }}>{s.desc}</p>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div style={{ textAlign: "center", marginTop: 36 }}>
              <a href="#contact" className="thm-btn agency-btn"
                style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                <span className="text">Book a Free Discovery Call</span>
              </a>
            </div>
          </div>
        )}

      </div>
    </section>
  );
};

export default AboutSection;
